import { useCallback, useEffect, useRef } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button, F0TagStatus } from '@factorialco/f0-react'
import { Dropdown } from '@factorialco/f0-react/dist/experimental'
import { Link, SolidPause, SolidPlay } from '@factorialco/f0-react/icons/app'
import { useDataChannel, useRemoteParticipants } from '@livekit/components-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useMeeting } from 'modules/meetings/lib/hooks/useMeeting'
import { useRoom } from 'modules/meetings/lib/hooks/useRoom'
import { useTranscript } from 'modules/meetings/lib/hooks/useTranscript'
import { formatDateToString, formatTimeToString } from 'modules/meetings/lib/utils/dates'

export const RoomHeader = () => {
  const { meeting } = useMeeting()
  const { isConnected } = useTranscript()
  const { isTranscriptEnabled, setIsTranscriptEnabled } = useRoom()
  const remoteParticipants = useRemoteParticipants()
  const previousParticipantCountRef = useRef(remoteParticipants.length)
  const {
    access: { locale },
  } = useCurrentSession()

  const onTranscriptMessage = useCallback(
    (msg: { payload: Uint8Array }) => {
      const decoder = new TextDecoder()
      const message = decoder.decode(msg.payload)
      try {
        const data = JSON.parse(message)
        if (data.type === 'transcript-control' && typeof data.enabled === 'boolean') {
          setIsTranscriptEnabled(data.enabled)
        }
      } catch {}
    },
    [setIsTranscriptEnabled]
  )

  const { send: sendTranscriptState } = useDataChannel('transcript-control', onTranscriptMessage)

  const sendTranscriptMessage = useCallback(
    (enabled: boolean) => {
      const message = JSON.stringify({ type: 'transcript-control', enabled })
      const encoder = new TextEncoder()
      sendTranscriptState(encoder.encode(message), { reliable: true })
    },
    [sendTranscriptState]
  )

  const handleTranscriptChange = useCallback(
    (enabled: boolean) => {
      setIsTranscriptEnabled(enabled)
      sendTranscriptMessage(enabled)
    },
    [setIsTranscriptEnabled, sendTranscriptMessage]
  )

  useEffect(() => {
    const currentCount = remoteParticipants.length
    const previousCount = previousParticipantCountRef.current

    if (currentCount > previousCount && !isTranscriptEnabled) {
      const timeoutId = setTimeout(() => {
        sendTranscriptMessage(false)
      }, 1000)

      return () => clearTimeout(timeoutId)
    }

    previousParticipantCountRef.current = currentCount
  }, [remoteParticipants.length, sendTranscriptMessage, isTranscriptEnabled])

  const link = window.location.href

  const handleCopyLink = () => {
    try {
      navigator.clipboard.writeText(link)
      app.setFlash({
        type: 'correct',
        message: i18n.t('meetings.room.header.copy_link_success'),
      })
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('meetings.room.header.copy_link_error'),
      })
    }
  }

  return (
    <Box
      paddingBottom="s16"
      paddingTop="s20"
      flexDirection="row"
      justifyContent="spaceBetween"
      alignItems="center"
      width="full"
    >
      <Text.Normal weight="semibold" size="300" color="grey1000" ellipsis>
        {meeting?.title}, {formatDateToString(meeting?.startTime, locale)} (
        {formatTimeToString(meeting?.startTime, locale)})
      </Text.Normal>

      <Box flexDirection="row" gap="s4" alignItems="center">
        <F0TagStatus
          text={i18n.t('meetings.room.transcript.label')}
          variant={isConnected && isTranscriptEnabled ? 'positive' : 'neutral'}
        />
        <Box
          background="grey400"
          style={{
            width: '1px',
            height: '16px',
            margin: '0 8px',
          }}
          borderRadius={{ all: 'abs008' }}
        />
        <Box flexDirection="row" gap="s12">
          <F0Button
            label={i18n.t('meetings.room.header.copy_link_button')}
            onClick={handleCopyLink}
            variant="outline"
            icon={Link}
          />
          {/* TODO: use F0ButtonToggle for this */}
          <Dropdown
            items={[
              {
                icon: isTranscriptEnabled ? SolidPause : SolidPlay,
                description: isTranscriptEnabled
                  ? i18n.t('meetings.room.transcript.disable_transcript_description')
                  : i18n.t('meetings.room.transcript.enable_transcript_description'),
                label: isTranscriptEnabled
                  ? i18n.t('meetings.room.transcript.disable_transcript')
                  : i18n.t('meetings.room.transcript.enable_transcript'),
                onClick: () => handleTranscriptChange(!isTranscriptEnabled),
              },
            ]}
          />
        </Box>
      </Box>
    </Box>
  )
}
